<template>
  <div>
    <transition
      enter-active-class="transition-all ease-out-quad duration-500"
      leave-active-class="transition-all ease-in-quad duration-500"
      enter-class="opacity-0 blur-0"
      enter-to-class="opacity-100 blur-100"
      leave-class="opacity-100 blur-100"
      leave-to-class="opacity-0 blur-0"
    >
      <div
        class="justify-center sm:items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none blur"
        v-if="isOpen"
      >
        <div class="w-full h-full absolute" style="z-index:100"></div>
      </div>
    </transition>

    <transition
      enter-active-class="transform transition ease-in-out duration-500 sm:duration-700"
      leave-active-class="transform transition ease-in-out duration-500 sm:duration-700"
      enter-class="translate-x-full"
      enter-to-class="translate-x-0"
      leave-class="translate-x-0"
      leave-to-class="translate-x-full"
    >
      <div class="fixed inset-0 overflow-hidden z-50" v-if="isOpen">
        <div class="absolute inset-0 overflow-hidden" @click.self="close">
          <section
            class="absolute inset-y-0 right-0 md:pl-10 max-w-full flex sm:pl-16"
          >
            <div class="w-screen max-w-3xl">
              <div
                class="h-full flex flex-col bg-white shadow-xl overflow-y-scroll md:overflow-hidden"
              >
                <div class="flex-1 flex flex-col md:h-screen">
                  <!-- Header -->
                  <header class="px-4 py-6 bg-gray-800 sm:px-6">
                    <div class="flex items-start justify-between space-x-3">
                      <div class="space-y-1 flex flex-col">
                        <div class="text-lg text-white">
                          {{ video.title }}
                        </div>
                        <div class="text-gray-300">
                          {{ video.user.name }}
                        </div>
                      </div>
                      <div class="h-7 flex items-center">
                        <button
                          class="p-1 border-2 border-transparent rounded-full hover:text-white focus:outline-none focus:text-white focus:bg-gray-700 text-gray-400 transition ease-in-out duration-150"
                          aria-label="Close panel"
                          v-on:click="close"
                        >
                          <svg
                            class="h-6 w-6"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </header>

                  <!-- Divider container -->
                  <div
                    class="flex-1 flex-col space-y-4 md:overflow-y-auto pb-4"
                  >
                    <YouTubePlayer :youtubeId="video.youtube_id" />
                    <div
                      class="grid grid-cols-1 md:grid-cols-3 gap-4 mx-4 my-4"
                    >
                      <div
                        class="p-6 border-2 rounded-lg flex flex-col items-center content-center"
                      >
                        <span class="text-xl md:text-2xl font-bold"
                          >${{ video.networkDisplayPrice }}</span
                        >
                        <span
                          class="mt-1 uppercase text-gray-400 font-medium text-sm"
                          >price</span
                        >
                      </div>
                      <div
                        class="p-6 border-2 rounded-lg flex flex-col items-center content-center"
                      >
                        <span class="text-xl md:text-2xl font-bold">
                          <StarRating
                            :id="video.id + '-vp'"
                            :rating="video.rating"
                          />
                        </span>
                        <span
                          class="mt-1 uppercase text-gray-400 font-medium text-sm"
                          >rating</span
                        >
                      </div>
                      <div
                        class="p-6 border-2 rounded-lg flex flex-col items-center content-center"
                      >
                        <span class="text-xl md:text-2xl font-bold">
                          {{
                            video.created_at | displayLocalDateTime("M/D/YY")
                          }}
                        </span>
                        <span
                          class="mt-1 uppercase text-gray-400 font-medium text-sm"
                          >posted date</span
                        >
                      </div>
                    </div>

                    <div class="flex flex-col mx-4 my-4">
                      <span
                        class="uppercase text-gray-400 font-semibold text-xs"
                        >Narrative</span
                      >
                      <p>
                        {{ video.description }}
                      </p>
                    </div>

                    <div class="flex flex-col mx-4 my-4">
                      <span
                        class="uppercase text-gray-400 font-semibold text-xs"
                        >Locations</span
                      >
                      <div>
                        <span
                          class="rounded px-2 py-1 bg-green-500 text-white text-xs mr-2 inline-block"
                          v-for="(location, index) in video.locations"
                          v-bind:key="index"
                          >{{ location }}</span
                        >
                        <span v-if="video.locations.length == 0"
                          >Check video title.</span
                        >
                      </div>
                    </div>
                  </div>

                  <div class="p-4 flex flex-col border-t-2 space-y-2">
                    <span
                      class="uppercase font-medium"
                      v-if="userInfo.network.creditAgreement !== null"
                      >purchasing options</span
                    >

                    <div
                      v-if="
                        userInfo.network.creditAgreement !== null &&
                          (video.exempt_from_credit_agreements ||
                            video.user.exempt_from_credit_agreements)
                      "
                    >
                      This video is unable to be purchased using credits due to
                      restrictions.
                      <span
                        v-if="
                          userInfo.network.creditAgreement
                            .creditUsageDataStatistics.creditsRemaining -
                            video.credit <
                            0
                        "
                        >This video is unable to be purchased using credits. The
                        current network agreement does not have enough free
                        credits to cover this transaction. You can however pay
                        the listed price until a new agreement is
                        established.</span
                      >
                    </div>

                    <!-- Show this if out of credits OR 
                    video is not for credit pricing -->

                    <div
                      class="grid gap-4"
                      :class="{
                        'grid-cols-1 md:grid-cols-2':
                          userInfo.network.creditAgreement !== null,
                        'grid-cols-1': userInfo.network.creditAgreement == null
                      }"
                      v-if="!video.purchased"
                    >
                      <button
                        class="w-full px-2 py-3 bg-blue-600 text-white rounded-lg focus:outline-none active:bg-blue-500 transition-all disabled:cursor-not-allowed disabled:opacity-50"
                        v-on:click="purchaseUsingCredits(video)"
                        v-if="userInfo.network.creditAgreement !== null"
                        :disabled="
                          userInfo.network.creditAgreement
                            .creditUsageDataStatistics.creditsRemaining -
                            video.credit <
                            0 ||
                            video.exempt_from_credit_agreements ||
                            video.user.exempt_from_credit_agreements
                        "
                      >
                        Purchase Video ({{ video.credit }} Credits)
                      </button>

                      <button
                        class="w-full px-2 py-3 bg-blue-600 text-white rounded-lg focus:outline-none active:bg-blue-500 transition-all"
                        v-on:click="purchase(video)"
                      >
                        Purchase Video
                        <span v-if="userInfo.network.creditAgreement !== null"
                          >(Post Billing)</span
                        >
                      </button>
                    </div>
                    <button
                      class="w-full px-2 py-3 bg-blue-600 text-white rounded-lg focus:outline-none active:bg-blue-500 transition-all"
                      v-on:click="downloadVideo(video.id)"
                      v-if="video.purchased"
                    >
                      Download Video
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import * as Config from "@/config";
import { mapGetters } from "vuex";
import YouTubePlayer from "./YouTubePlayer";
import StarRating from "@/components/general/StarRating";

export default {
  name: "MediaVideoPopOver",
  props: {
    isOpen: Boolean
  },
  components: { YouTubePlayer, StarRating },
  data() {
    return {
      video: null
    };
  },
  created() {
    document.addEventListener("keyup", this.handleKeyUpEvent);
    this.$eventBus.$on("openSlideOver", this.open);
  },
  beforeDestroy() {
    document.removeEventListener("keyup", () => {});
    this.$eventBus.$off("openSlideOver", this.open);
  },
  computed: {
    ...mapGetters(["userInfo"])
  },
  methods: {
    handleKeyUpEvent(event) {
      if (this.isOpen && event.keyCode === 27) this.close();
    },
    close() {
      if (this.isOpen) this.$emit("closePopover");
    },
    open(data) {
      this.video = data;
    },
    async purchaseUsingCredits(video) {
      let result = await this.$swal({
        title: "Continue?",
        text:
          "You are about to purchase " +
          video.title +
          " for " +
          video.credit +
          " credits.",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, purchase and download."
      });

      if (result.isConfirmed) {
        var data = {
          id: video.id,
          creditAgreementId: this.userInfo.network.creditAgreement.id
        };
        let res = await this.$http.post(
          Config.apiPath + "/api/v2/media/purchases/creditPurchase",
          data
        );
        var link = document.createElement("a");
        document.body.appendChild(link);
        link.href = res.data;
        link.click();
        document.body.removeChild(link);

        video.purchased = true;
        this.$swal("All good!", "The video download has started.", "success");
      }
    },
    async purchase(video) {
      let result = await this.$swal({
        title: "Continue?",
        text:
          "You are about to purchase " +
          video.title +
          " for $" +
          video.networkDisplayPrice +
          ".",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, purchase and download."
      });
      if (result.isConfirmed) {
        var data = {
          id: video.id
        };
        let res = await this.$http.post(
          Config.apiPath + "/api/v2/media/purchases/new",
          data
        );
        var link = document.createElement("a");
        document.body.appendChild(link);
        link.href = res.data;
        link.click();
        document.body.removeChild(link);

        video.purchased = true;
        this.$swal("All good!", "The video download has started.", "success");
      }
    },
    downloadVideo(id) {
      var self = this;
      this.$http
        .get(Config.apiPath + "/api/v2/media/purchases/download/" + id)
        .then(response => {
          if (response.data !== 400) {
            var link = document.createElement("a");
            document.body.appendChild(link);
            link.href = response.data;
            link.click();
            document.body.removeChild(link);
          } else {
            self.$swal(
              "Uh oh!",
              "There has been an issue. It looks like you don't own this video. If this is a mistake, please open a support ticket or contact Brett Adair.",
              "error"
            );
          }
        });
    }
  }
};
</script>
